<template>
  <div>
    <div class="row">
      <div class="col-xl-4 col-lg-6 position-static">
        <label class="col-form-label col-form-label-sm">
          {{ $t('Webportal.Category') }}
          <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Category.Description')">
            <font-awesome-icon :icon="['fas', 'info-circle']" />
          </span>
        </label>
        <b-input-group class="p-0 mb-3 position-static">
          <b-form-input size="sm" v-model="reportFileCategory" autocomplete="off" :disabled="disabled" :maxlength="30" />
          <template #append>
            <b-dropdown size="sm" id="reportfilecategory-dropdown" boundary="window" :disabled="disabled || reportFileCategoriesInternal.length == 0">
              <b-dropdown-item
                v-for="rft in reportFileCategoriesInternal"
                :key="rft.Name"
                @click="reportFileCategory = rft.Name">
                {{ rft.Name }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-input-group>
      </div>
      <div class="col-xl-8 col-lg-6 position-relative">
        <label class="col-form-label col-form-label-sm">{{ $t('Webportal.Location') }}</label>

        <treeselect v-if="clientLocations && clientLocations.length > 0"
                    ref="clientLocationTreeselect"
                    :placeholder="$t('Webportal.LocationFilter.Placeholder')"
                    class="mb-3 pe-2"
                    :multiple="true"
                    value-format="object"
                    instance-id="clientLocations"
                    v-model="selectedClientLocations"
                    :options="clientLocations"
                    :auto-load-root-options="false"
                    :disabled="disabled"
                    :disableFuzzyMatching="true"

                    :async="isClientLocationsLimitExceeded"
                    :search-prompt-text="clientLocationsSearchPromptText"
                    :load-options="loadClientLocationsOptions"

                    :showCount="true"
                    showCountOf="LEAF_DESCENDANTS"
                    :defaultExpandLevel="1"
                    :disable-branch-nodes="false"
                    value-consists-of="LEAF_PRIORITY"
                    :limit="4"
                    :limit-text="limitText" />

        <div v-else-if="isBusy" class="mb-3">
          <b-spinner small variant="primary"></b-spinner>
        </div>
        <div v-else class="mb-3">
          {{ $t('Webportal.NoDataFound')}}
        </div>
      </div>
    </div>

    <!-- Custom Properties input style switch -->
    <div class="row">
      <label class="col-auto col-form-label col-form-label-sm">{{ $t('Webportal.Properties') }}</label>
      <b-form-radio-group class="col-auto mb-3"
                          size="sm"
                          buttons button-variant="outline-primary"
                          v-model="expandProperties">
        <b-form-radio :value="false">
          <font-awesome-icon fixed-width :icon="['far', 'rectangle-wide']" />
        </b-form-radio>
        <b-form-radio :value="true">
          <font-awesome-icon fixed-width :icon="['far', 'list-alt']" />
        </b-form-radio>
      </b-form-radio-group>
    </div>

    <!-- Custom Properties Input -->
    <div v-if="!expandProperties" class="mb-3">
      <treeselect v-if="customProperties && customProperties.length > 0"
                  ref="propertiesTreeselect"
                  instance-id="properties"
                  v-model="selectedCustomProperties"
                  :options="customProperties"
                  :auto-load-root-options="false"
                  :disabled="disabled"
                  :disableFuzzyMatching="true"
                  multiple
                  :showCount="true"
                  showCountOf="LEAF_DESCENDANTS"
                  :defaultExpandLevel="1"
                  :disable-branch-nodes="false"
                  value-consists-of="LEAF_PRIORITY"
                  :limit="10"
                  :limit-text="limitText" />
      <div v-else-if="isBusy" class="mb-3">
        <b-spinner small variant="primary"></b-spinner>
      </div>
      <div v-else class="mb-3">
        {{ $t('Webportal.NoDataFound')}}
      </div>
    </div>

    <custom-properties-input  v-else
                              :disabled="disabled"
                              v-model="propertyIds"
                              :client-id="clientId"
                              :only-visible="!userCanUseHiddenCustomProperties"
                              :expand-properties="true" />
  </div>
</template>

<script>
import axios from 'axios';
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import ReportFileMixin from '@/mixins/ReportFileMixin';
import CustomPropertiesInput from "@/components/Shared/CustomPropertiesInput";
import CustomTreeSelect from '@/components/Shared/CustomTreeSelect';

import AdministrationService from '@/services/AdministrationService';
import ReportFileService from '@/services/ReportFileService';
import SurveysService from "@/services/SurveysService";

export default {
  name: "ReportFileProperties",

  mixins: [ AppConfig, ReportFileMixin ],

  emits: ["onReportFilePropertiesChange"],

  components: { CustomPropertiesInput, CustomTreeSelect },

  props: {
    value: { type: Object, default: () => {} },
    disabled: { type: Boolean, default: false },
    clientId: { type: Number, default: null },
    reportFiles: { type: Array, default: () => [] },
    reportFileCategories: { type: Array, default: () => [] },
    lockProperties: { type: Boolean, default: false },
  },

  data() {
    return {
      expandProperties: false,
      propertyIds: [],

      clientLocations: [],
      clientLocationsCancelToken: null,
      selectedClientLocations: [],
      clientLocationsOptionsLimit: 500,

      reportFileCategory: null,
      reportFileCategoriesCancelToken: null,
      reportFileCategoriesInternal: this.reportFileCategories,

      customProperties: [],
      customPropertiesCancelToken: null,
      selectedCustomProperties: [],

      isBusy: false,
    };
  },

  computed: {
    isClientLocationsLimitExceeded() {
      return this.selectedClientLocations?.length > this.clientLocationsOptionsLimit;
    },

    clientLocationsSearchPromptText() {
      return this.$t('Webportal.ItemCountExceedsThreshold', { count: this.selectedClientLocations?.length, threshold: this.clientLocationsOptionsLimit });
    }
  },

  watch: {
    reportFileCategory(newVal) {
      this.emitChange();
    },
    propertyIds(newVal) {
      this.emitChange();
    },
    selectedClientLocations(newVal) {
      this.emitChange();
    },
    selectedCustomProperties(newVal, oldVal) {
      this.emitChange();
    },

    clientId() {
      this.isBusy = true;
      this.clientLocations = [];
      this.selectedClientLocations = [];
      this.reportFileCategoriesInternal = [];
      this.reportFileCategory = null;

      this.customProperties = [];
      this.selectedCustomProperties = [];


      this.getClientLocations();
      this.getReportFileCategories();
      this.getSurveyProperties();
    },

    reportFiles(newVal, oldVal) {
      if (!this.lockProperties && !_.isEqual(newVal, oldVal)) {

        if (newVal?.ReportFileCategory)
          this.reportFileCategory = [newVal.ReportFileCategory.Name];
        else
          this.reportFileCategory = [];

        if (newVal?.Properties)
          this.propertyIds = _.uniq(newVal.Properties.flatMap(rf => rf.PropertyValueIds));
        else
          this.propertyIds = null;

        if (newVal?.ClientLocations && !_.isEmpty(newVal.ClientLocations)) {
          this.selectedClientLocations = _.uniqBy(
            newVal.ClientLocations.map(this.getClientLocationItem),
            item => item.id
          );
        } else {
          this.selectedClientLocations = [];
        }

        // this.selectedClientLocations = _.uniqBy(newVal
        //   .filter(rf => !_.isEmpty(rf.ClientLocations))
        //   .map(rf => rf.ClientLocations.map(this.getClientLocationItem))
        //   .flat(),
        //   item => item.id);
      }
    },

    reportFileCategories(newVal) {
      this.reportFileCategoriesInternal = newVal;
    },
  },

  methods: {
    loadClientLocationsOptions({ action, searchQuery, callback }) {
      if (action === 'ASYNC_SEARCH') {
        searchQuery = searchQuery.toLowerCase();
        const options = this.clientLocations.filter(item => item.label.toLowerCase().includes(searchQuery));

        if (options.length <= this.clientLocationsOptionsLimit)
          callback(null, options);
        else
          callback(this.$t('Webportal.ItemCountExceedsThreshold',
            { count: options.length, threshold: this.clientLocationsOptionsLimit }));
      } else {
        callback();
      }
    },

    emitChange() {
      this.$emit("onReportFilePropertiesChange", {
        reportFileCategory: this.reportFileCategory,
        propertyIds: this.selectedCustomProperties,
        clientLocationIds: this.selectedClientLocations?.map(item => item.id)
      });
    },

    getClientLocationItem(clientLocation) {
      return {
        id: clientLocation.ClientLocationId,
        label: [
          clientLocation.ClientLocationIdOnClientSite ?? '',
          clientLocation.Name ?? '',
          clientLocation.PostalCode ?? '',
          clientLocation.City ?? ''
        ].join(' ')
      };
    },

    getClientLocations() {
      if (this.clientId) {
        this.isBusy = true;

      // Cancel previous request
      if (this.clientLocationsCancelToken) {
        this.clientLocationsCancelToken.cancel();
      }

      this.clientLocationsCancelToken = axios.CancelToken.source();
      AdministrationService.getClientLocations({ clientIds: [this.clientId] }, {}, this.clientLocationsCancelToken)
        .then(response => {
          this.clientLocations = response.data.items.map(item => this.getClientLocationItem(item.ClientLocation)) ?? [];
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            console.error("Error loading client locations: ", error);
          }
        })
        .finally(_ => {
          this.isBusy = false;
        });
      }
    },

    getReportFileCategories() {
      if (!this.clientId)
        return;

      // Cancel previous request
      if (this.reportFileCategoriesCancelToken)
        this.reportFileCategoriesCancelToken.cancel();

      this.reportFileCategoriesCancelToken = axios.CancelToken.source();

      ReportFileService.getReportFileCategories(this.clientId, this.reportFileCategoriesCancelToken)
        .then(response => {
          this.reportFileCategoriesInternal = response.data;
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            console.error("Error loading report file categories: ", error);
          }
        });
    },

    getSurveyProperties() {
      if (!this.clientId)
        return;

      this.isBusy = true;

      // Cancel previous request
      if (this.propertiesCancelToken) {
        this.propertiesCancelToken.cancel();
      }

      this.propertiesCancelToken = axios.CancelToken.source();
      SurveysService.getSurveyCustomProperties({ clientIds: [ this.clientId ] }, true, false, this.onlyVisible, true, this.propertiesCancelToken)
        .then((response) => {
          if (response.data && response.data.length > 0) {
            var propertyTypeMapping = {
              'true': { id: 'T1', label: this.$t('Webportal.Properties.Visible'), isVisible: true },
              'false': { id: 'T2', label: this.$t('Webportal.Properties.Hidden'), isVisible: false }
            };

            this.customProperties = _(response.data[0].children)
              .groupBy(p => p.IsVisible ?? true) // IsVisible is null when parameter @OnlyVisible is set to true
              .map((items, key) => ({
                id: propertyTypeMapping[key].id,
                label: propertyTypeMapping[key].label,
                isVisible: propertyTypeMapping[key].isVisible,
                children: items.map(p => ({
                  id: p.id,
                  label: p.label,
                  isVisible: propertyTypeMapping[key].isVisible,
                  children: p.children.map(pv => ({
                    id: pv.id,
                    label: pv.label,
                    property: p
                  }))
                }))
              }))
              .orderBy(pt => pt.isVisible, 'desc')
              .value();
          }
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            console.error("Error loading custom properties: ", error);
          }
        })
        .finally(_ => {
          this.isBusy = false;
        });
    },
  },
};
</script>

<style>
  #reportfilecategory-dropdown .dropdown-menu {
      max-height: 400px;
      overflow-y: auto;
  }
</style>
